import React, { FC, useContext, useId, useState } from 'react';

import { ButtonTypes, LinkButton, StandardButton } from '@eika/button';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { ExpandableInput } from '@eika/expandable-input';
import { IconTypes } from '@eika/icon';
import { Modal } from '@eika/modal';

import { AppContext } from '../../containers/appContextContainer';
import { useDeleteSoeknad } from '../../hooks/useSoeknadMedlaantaker';
import { useTranslation } from '../../translations';

export const BOLIGLAAN_LAANEFORMAAL_URL = `${window.location.origin}/lan/boliglan/behov`;

interface Props {
    soeknadId: string | null | undefined;
    showModal: boolean;
    setShowModal: (showModal: boolean) => void;
}

const UserMessageDeleteSoeknad: FC<Props> = ({ soeknadId, showModal, setShowModal }) => {
    const { button, deleteSoeknad } = useTranslation();
    const { appNameSpace } = useContext(AppContext);
    const INNHOLD = {
        FORSIDE: 'FORSIDE',
        BEKREFT_SLETTING: 'BEKREFT_SLETTING',
        SLETTING_GJENNOMFOERT: 'SLETTING_GJENNOMFOERT',
    };
    const [kommentar, setKommentar] = useState<string | null>('');
    const [modalInnhold, setModalInnhold] = useState<string>(INNHOLD.FORSIDE);
    const id = useId();
    const { mutate: DeleteSoeknad } = useDeleteSoeknad(() => {
        setModalInnhold(INNHOLD.SLETTING_GJENNOMFOERT);
    });

    const onBekreftDeleteSoeknad = () => {
        if (soeknadId) {
            DeleteSoeknad({ soeknadId, deleteReason: kommentar });
        } else {
            setModalInnhold(INNHOLD.SLETTING_GJENNOMFOERT);
        }
    };

    const onClickLagreOgFortsett = () => {
        const redirectUrl = window.location.origin;
        window.location.replace(redirectUrl);
    };

    const onCloseModal = () => {
        setShowModal(false);
        setModalInnhold(INNHOLD.FORSIDE);
    };

    const onDeleteFerdig = () => {
        window.location.replace(BOLIGLAAN_LAANEFORMAAL_URL);
        onCloseModal();
    };

    const finnTittel = (modalInnhold: string) => {
        switch (modalInnhold) {
            case INNHOLD.FORSIDE:
                return deleteSoeknad.title;
            case INNHOLD.BEKREFT_SLETTING:
                return deleteSoeknad.titleBekreft;
            default:
                return '';
        }
    };

    return (
        <Modal
            id="deleteSoeknadModal"
            title={finnTittel(modalInnhold)}
            description={modalInnhold === INNHOLD.FORSIDE ? deleteSoeknad.subTitle : ''}
            appNamespace={appNameSpace}
            setOpen={setShowModal}
            open={showModal}
            onClose={modalInnhold === INNHOLD.SLETTING_GJENNOMFOERT ? onDeleteFerdig : onCloseModal}
        >
            {modalInnhold === INNHOLD.SLETTING_GJENNOMFOERT ? (
                <div className="row row-gap align-items margin-top-extra-large">
                    <CheckmarkAnimation key={id} />
                    <h2>{deleteSoeknad.titleSoeknadSlettet}</h2>
                    <p style={{ textAlign: 'center' }} className="typography-paragraph">
                        {deleteSoeknad.soeknadErSlettet}
                    </p>
                    <LinkButton onClick={onDeleteFerdig}>{button.startSoeknadPaaNytt}</LinkButton>
                </div>
            ) : (
                <div className="row row-gap align-items margin-top-extra-large">
                    {modalInnhold === INNHOLD.FORSIDE && (
                        <div className="column column-gap">
                            <StandardButton
                                onClick={() => setModalInnhold(INNHOLD.BEKREFT_SLETTING)}
                                icon={IconTypes.SLETT_24}
                                buttonType={ButtonTypes.STANDARD_NEGATIVE}
                            >
                                {button.slettSoeknad}
                            </StandardButton>
                            <StandardButton buttonType={ButtonTypes.STANDARD_POSITIVE} onClick={onClickLagreOgFortsett}>
                                {button.lagreOgFortsett}
                            </StandardButton>
                        </div>
                    )}
                    {modalInnhold === INNHOLD.BEKREFT_SLETTING && (
                        <div className="row row-gap">
                            <div className="typography-subtitle">{deleteSoeknad.subTitleBekreft}</div>
                            <ExpandableInput
                                value={kommentar || ''}
                                onChange={(event) => setKommentar(event.target.value)}
                                style={{ marginBottom: '50px' }}
                            />
                            <StandardButton
                                onClick={onBekreftDeleteSoeknad}
                                icon={IconTypes.SLETT_24}
                                buttonType={ButtonTypes.STANDARD_POSITIVE}
                                style={{ maxWidth: '50%', alignSelf: 'center' }}
                            >
                                {button.bekreftSlettSoeknad}
                            </StandardButton>
                        </div>
                    )}
                    <LinkButton
                        data-track-id="kreditt-boliglaan-slett-soeknad-modal"
                        data-track-linkname="lukk"
                        onClick={() => setShowModal(false)}
                    >
                        {button.gaaTilbakeTilSoeknad}
                    </LinkButton>
                </div>
            )}
        </Modal>
    );
};

export default UserMessageDeleteSoeknad;
